<template>
  <div>
    <!-- {{ orderEntries }} -->
    <div>
      <b-row class="match-height">
        <b-col
          xl="3"
          md="6"
          sm="6"
        >
          <statistic-card-vertical
            color="warning"
            icon="UsersIcon"
            :statistic="filteredOrders.length"
            :statistic-title="getStatisticTitle('orders')"
          />
        </b-col>
        <b-col
          xl="3"
          md="6"
          sm="6"
        >
          <statistic-card-vertical
            color="success"
            icon="DollarSignIcon"
            :statistic="monthlyRecurringRevenue"
            :statistic-title="getStatisticTitle('revenue')"
          />
        </b-col>
        <b-col
          xl="3"
          md="6"
          sm="6"
        >
          <statistic-card-vertical
            color="info"
            icon="TrendingUpIcon"
            :statistic="averageOrderValue"
            statistic-title="⌀ Bestellwert"
          />
        </b-col>
        <b-col
          xl="3"
          md="6"
          sm="6"
        >
          <statistic-card-vertical
            color="primary"
            icon="PackageIcon"
            :statistic="totalOrderItems"
            :statistic-title="getStatisticTitle('positions')"
          />
        </b-col>
      </b-row>
    </div>

    <b-card no-body>
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label class="mr-1">Zeige</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-1"
              style="min-width: 80px; max-width: 100px;"
            />
            <label class="ml-1">Einträge</label>
          </b-col>

          <!-- Search -->
          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="filter"
                class="d-inline-block mr-1"
                placeholder="Suche..."
                type="search"
              />
            </div>
          </b-col>
        </b-row>

        <b-tabs
          v-model="currentTab"
          class="mt-2 custom-tabs"
        >
          <b-tab :title="`Alle (${orderEntries.length})`" />
          <b-tab :title="`Aktive Buchungen (${activeCount})`" />
          <b-tab :title="`Überfällige Buchungen (${exceededCount})`" />
          <b-tab :title="`Ausgebuchte Buchungen (${checkedOutCount})`" />
        </b-tabs>
      </div>

      <!-- Tabelle (Inhalt je nach Tab) -->
      <b-table
        ref="refListTable"
        striped
        hover
        responsive
        :items="filteredOrders"
        :fields="fields"
        :filter="filter"
        :current-page="currentPage"
        :per-page="perPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        show-empty
        empty-text="Keine Einträge gefunden"
        @row-clicked="onRowClicked"
      >
        <template #cell(orderDate)="data">
          {{ getFormattedDate(data.item.orderDate) }}
        </template>

        <template #cell(customer)="data">
          {{ data.value.firstName }} {{ data.value.lastName }}
        </template>

        <template #cell(order_items)="data">
          <b-badge variant="primary">
            {{ data.value ? data.value.length : 0 }}
          </b-badge>
        </template>

        <template #cell(price)="data">
          <b-badge variant="success">
            {{
              data.item.order_items
                ? data.item.order_items.reduce(
                  (acc, curr) => acc + curr.price,
                  0
                )
                : 0
            }}
            €
          </b-badge>
        </template>

        <template #cell(status)="data">
          <b-badge
            :variant="getRentalStatus(data.item).variant"
            class="rental-status-badge"
          >
            <feather-icon
              :icon="getRentalStatus(data.item).icon"
              size="12"
              class="mr-1"
            />
            {{ getRentalStatus(data.item).label }}
          </b-badge>
        </template>

        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item :to="`/orders-view/${data.item.id}`">
              <feather-icon icon="FileTextIcon" />
              <span class="align-middle ml-50">Details</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>

      <!-- Pagination -->
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">
              Zeige {{ dataMeta.from }} bis {{ dataMeta.to }} von {{ getTotalRows }} Einträgen
            </span>
          </b-col>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="getTotalRows"
              :per-page="perPage"
              first-number
              last-number
              prev-class="prev-item"
              next-class="next-item"
              class="mb-0"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BTable,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BPagination,
  BBadge,
  BDropdown,
  BDropdownItem,
  BTabs,
  BTab,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import helper from '@/helper/helper'
import businessMetrics from '@/helper/businessMetrics'
import StatisticCardVertical from '@core/components/statistics-cards/StatisticCardVertical.vue'
import tableHelper from '../../Table+Helper'

export default {
  setup() {
    const { getFormattedDate, formatGermanCurrency } = helper()
    const { getCurrentMRR, getCurrentAverageMonthlyRate } = businessMetrics()

    const {
      perPageOptions,
      perPage,
      filter,
      totalRows,
      currentPage,
      refListTable,
      dataMeta,
      refetchData,
    } = tableHelper()

    return {
      perPageOptions,
      perPage,
      filter,
      totalRows,
      currentPage,
      refListTable,
      dataMeta,
      getFormattedDate,
      formatGermanCurrency,
      refetchData,
      getCurrentMRR,
      getCurrentAverageMonthlyRate,
    }
  },
  components: {
    BTable,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BPagination,
    BBadge,
    vSelect,
    StatisticCardVertical,
    BDropdown,
    BDropdownItem,
    BTabs,
    BTab,
  },
  data() {
    return {
      sortBy: 'id',
      sortDesc: true,
      currentTab: 0, // 0 = Active, 1 = Checked out
      fields: [
        {
          key: 'id',
          label: 'ID',
          sortable: true,
        },
        {
          key: 'orderDate',
          label: 'Buchungsdatum',
          sortable: true,
        },
        {
          key: 'orderDetails',
          label: 'Buchungsdetails',
        },
        {
          key: 'customer',
          label: 'Kunde',
        },
        {
          key: 'order_items',
          label: 'Positionen',
          sortable: true,
        },
        {
          key: 'price',
          label: 'Summe',
          sortable: true,
        },
        {
          key: 'status',
          label: 'Status',
          sortable: false,
        },
        { key: 'actions', label: 'Aktionen' },
      ],
    }
  },
  computed: {
    orderEntries() {
      return this.$store.getters.orderEntries
    },
    // Filter orders based on their checkout status
    activeOrders() {
      const now = new Date()
      return this.orderEntries.filter(order => order.order_items?.some(item => !item.moveOutDate && (!item.rentEnd || new Date(item.rentEnd) >= now)))
    },
    exceededOrders() {
      const now = new Date()
      return this.orderEntries.filter(order => order.order_items?.some(item => !item.moveOutDate && item.rentEnd && new Date(item.rentEnd) < now))
    },
    checkedOutOrders() {
      return this.orderEntries.filter(order => order.order_items?.length > 0 && order.order_items.every(item => item.moveOutDate))
    },
    // Orders to display based on current tab
    filteredOrders() {
      if (this.currentTab === 0) return this.orderEntries
      if (this.currentTab === 1) return this.activeOrders
      if (this.currentTab === 2) return this.exceededOrders
      return this.checkedOutOrders
    },
    // Tab counts
    activeCount() {
      return this.activeOrders.length
    },
    exceededCount() {
      return this.exceededOrders.length
    },
    checkedOutCount() {
      return this.checkedOutOrders.length
    },
    getTotalRows() {
      return this.filteredOrders.length
    },
    monthlyRecurringRevenue() {
      // Show current MRR consistently across all tabs
      const value = Math.round(this.getCurrentMRR())
      return this.formatGermanCurrency(value)
    },
    averageOrderValue() {
      const currentOrders = this.filteredOrders
      if (currentOrders.length === 0) return this.formatGermanCurrency(0)
      const totalBookingValue = currentOrders.reduce((total, order) => {
        if (order.order_items && order.order_items.length > 0) {
          return total + order.order_items.reduce((orderTotal, item) => orderTotal + item.price, 0)
        }
        return total
      }, 0)
      const value = Math.round(totalBookingValue / currentOrders.length)
      return this.formatGermanCurrency(value)
    },
    totalOrderItems() {
      return this.filteredOrders.reduce((total, order) => total + (order.order_items ? order.order_items.length : 0), 0)
    },
  },
  watch: {
    // Watch search input and reset pagination when it changes
    filter: {
      handler() {
        this.currentPage = 1
      },
    },
    // Watch tab changes and reset pagination
    currentTab() {
      this.currentPage = 1
    },
  },
  mounted() {
    this.totalRows = this.getTotalRows
  },
  methods: {
    onRowClicked(item) {
      this.$router.push(`/orders-view/${item.id}`)
    },
    getRentalStatus(order) {
      const now = new Date()

      // Check if all items are checked out
      const allCheckedOut = order.order_items?.every(item => item.moveOutDate)
      if (allCheckedOut) {
        return {
          label: 'Beendet',
          variant: 'secondary',
          icon: 'CheckCircleIcon'
        }
      }

      // Check if any item has exceeded rental period
      const hasExceeded = order.order_items?.some(item => !item.moveOutDate && item.rentEnd && new Date(item.rentEnd) < now)
      if (hasExceeded) {
        return {
          label: 'Überfällig',
          variant: 'danger',
          icon: 'AlertTriangleIcon'
        }
      }

      // Active rental within period
      return {
        label: 'Aktiv',
        variant: 'success',
        icon: 'CheckIcon'
      }
    },
    getStatisticTitle(type) {
      const titles = {
        orders: ['Alle Buchungen', 'Aktive Buchungen', 'Überfällige Buchungen', 'Ausgebuchte Buchungen'],
        revenue: ['Umsatz mtl.', 'Umsatz mtl.', 'Umsatz mtl.', 'Umsatz mtl.'],
        positions: ['Alle Positionen', 'Aktive Positionen', 'Überfällige Positionen', 'Abgeschlossene Positionen']
      }
      return titles[type][this.currentTab] || titles[type][0]
    },
  },
}
</script>

<style lang="scss" scoped>
.rental-status-badge {
  font-size: 0.8rem;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  font-weight: 500;

  &.badge-danger {
    animation: subtle-pulse 2s infinite;
  }
}

// Subtle animation for exceeded status
@keyframes subtle-pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

// Override table row styling for exceeded orders
::v-deep .table {
  tbody tr {
    &.exceeded-order {
      background-color: rgba(220, 53, 69, 0.05);

      &:hover {
        background-color: rgba(220, 53, 69, 0.1);
      }
    }
  }
}

// Tab styling
.custom-tabs {
  .nav-tabs {
    border-bottom: 1px solid #dee2e6;

    .nav-item {
      .nav-link {
        color: #6c757d;
        border: 1px solid transparent;
        border-top-left-radius: 0.375rem;
        border-top-right-radius: 0.375rem;

        &:hover {
          border-color: #e9ecef #e9ecef #dee2e6;
          color: #495057;
        }

        &.active {
          color: #495057;
          background-color: #fff;
          border-color: #dee2e6 #dee2e6 #fff;
        }
      }
    }
  }
}
</style>
